/**
 * <<uppercase_module_name>> Module
 */
import Vue from "vue";
import Vuex from "vuex";
import api from "Api";
import router from "Router";
import app from "Main";

// import the api endpoints
import {
    getEntityAddresses,
    getEntityAddress,
    printEntityAddress,
    createEntityAddresses,
    updateEntityAddresses,
    deleteEntityAddresses,
} from "Core/entities/config/api.entity_addresses";

const state = {
    page: {},
    form: {},
    entity_addresses: [],
    entity_address_options: [],
    selectedEntityAddress: {
        entity_addresses_id: null,
        company_id: "",
        entity_address_date: "",
        reference_number: "",
        status_id: "",
        billing_address_id: "",
        shipping_address_id: "",
        comments: "",
        entity_address_details: [],
    },
    entity_addresses_filter: {
        search: "",
        page_size: 2,
        page_num: 1,
    },
    rules_entity_addresses: {
        entity_addresses_id: {
            name: "entity_addresses_id",
            rules: "trim|max_length[15]|integer",
            max_len: "15",
            min_len: "",
            default_value: "",
        },
        object_ref: {
            name: "object_ref",
            rules: "trim|max_length[10]|required",
            max_len: "10",
            min_len: "",
            default_value: "",
        },
        object_id: {
            name: "object_id",
            rules: "trim|max_length[36]|required",
            max_len: "36",
            min_len: "",
            default_value: "",
        },
        location_label: {
            name: "location_label",
            rules: "trim|max_length[65]",
            max_len: "65",
            min_len: "",
            default_value: "",
        },
        address1: {
            name: "address1",
            rules: "trim|max_length[255]|required",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        address2: {
            name: "address2",
            rules: "trim|max_length[255]",
            max_len: "255",
            min_len: "",
            default_value: "",
        },
        city: {
            name: "city",
            rules: "trim|max_length[100]|required",
            max_len: "100",
            min_len: "",
            default_value: "",
        },
        state: {
            name: "state",
            rules: "trim|max_length[65]|required",
            max_len: "65",
            min_len: "",
            default_value: "",
        },
        zip_code: {
            name: "zip_code",
            rules: "trim|max_length[12]|required",
            max_len: "12",
            min_len: "",
            default_value: "",
        },
        zip_code_ext: {
            name: "zip_code_ext",
            rules: "trim|max_length[4]",
            max_len: "4",
            min_len: "",
            default_value: "",
        },
        country_id: {
            name: "country_id",
            rules: "trim|max_length[15]|integer|required",
            max_len: "15",
            min_len: "",
            default_value: "",
        },
        address_type: {
            name: "address_type",
            rules: "trim|max_length[36]|required",
            max_len: "36",
            min_len: "",
            default_value: "",
        },
        prime: {
            name: "prime",
            rules: "trim|max_length[1]|integer",
            max_len: "1",
            min_len: "",
            default_value: "",
        },
        new_prime_entity_addresses_id: {
            name: "new_prime_entity_addresses_id",
            rules: "trim|required",
            max_len: "",
            min_len: "",
            default_value: "",
        },
    },
};

// getters
const getters = {
    entity_addresses: (state) => {
        return state.entity_addresses;
    },
};

// actions
const actions = {
    async fetchEntityAddresses({ rootState, commit, dispatch }, payload = null) {
        try {
            let body = payload.body ? payload.body : "";
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            const response = await getEntityAddresses({ body: body, headers: config.headers });

            if (typeof response !== "undefined" && response.data) {
                if (response.data.page) {
                    commit("setPageEntityAddresses", response.data.page);
                }

                if (body.options) {
                    commit("setFePartOptions", response.data.fe_parts);
                    commit("setEntityAddressesOptions", response.data.entity_addresses);
                } else {
                    commit("setEntityAddresses", response.data.entity_addresses);
                }

                return response.data;
            } else {
                console.log("fetch all response error");
                return;
            }
        } catch (error) {
            console.log("fetch all error");
            return;
        }
    },
    async fetchEntityAddress({ rootState, commit, dispatch }, payload = null) {
        try {
            let response = null;
            let body = payload.body ? payload.body : "";
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await getEntityAddress(payload.id);

            if (typeof response !== "undefined" && response.data) {
                commit("setSelectedEntityAddress", response.data.entity_address);
                return response.data;
            } else {
                console.log("fetch response error");
                return;
            }
        } catch (error) {
            console.log("fetch single error");
            return;
        }
    },
    async printEntityAddress({ rootState, commit, dispatch }, payload = null) {
        try {
            let response = null;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await printEntityAddress(payload.id);

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("fetch print response error");
                return;
            }
        } catch (error) {
            console.log("print error");
            return;
        }
    },
    async saveEntityAddress({ rootState, commit, dispatch }, payload) {
        try {
            let response = null;
            let body = payload.body;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            if (typeof body["entity_addresses_id"] === "undefined" || body["entity_addresses_id"] == null || body["entity_addresses_id"] == "") {
                delete body["entity_addresses_id"];
                response = await createEntityAddresses({ body: body, headers: config.headers });
            } else {
                let entity_addresses_id = body["entity_addresses_id"];
                delete body["entity_addresses_id"];
                response = await updateEntityAddresses({ body: body, headers: config.headers }, entity_addresses_id);
            }

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("save response error");
                return;
            }
        } catch (error) {
            console.log("save error");
            return;
        }
    },
    async deleteEntityAddress({ rootState, commit, dispatch }, payload) {
        try {
            let response = null;
            let body = payload.body;
            let config = {
                headers: payload.headers ? payload.headers : {},
            };

            response = await deleteEntityAddresses(payload.id, payload.new_prime_entity_addresses_id);

            if (typeof response !== "undefined" && response.data) {
                return response.data;
            } else {
                console.log("delete response error");
                return;
            }
        } catch (error) {
            console.log("delete error");
            return;
        }
    },
};

// mutations
const mutations = {
    setPageEntityAddresses(state, page) {
        state.page = page;
    },
    setEntityAddresses(state, entity_addresses) {
        state.entity_addresses = entity_addresses;
    },
    setEntityAddressesOptions(state, entity_addresses) {
        state.entity_address_options = entity_addresses;
    },
    setSelectedEntityAddress(state, entity_address) {
        state.selectedEntityAddress = entity_address;
    },
    resetSelectedEntityAddress(state) {
        for (const key in state.selectedEntityAddress) {
            if (key in state.rules_entity_addresses) {
                state.selectedEntityAddress[key] =
                    state.rules_entity_addresses[key].default_value == "null" ? null : state.rules_entity_addresses[key].default_value;
            }
        }
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
